import { db } from './firebase';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import CryptoJS from 'crypto-js';
import type { ApiKeyProvider } from './api-keys';

const SALT = 'hivemind-secure-salt-v1'; // Application-level salt

export interface UserPreferences {
    encryptedApiKeys?: string;
    theme?: 'light' | 'dark' | 'system';
    onboardingCompleted?: boolean;
}

export const UserService = {
    /**
     * Encrypts API keys using AES with a key derived from the user's ID and app salt.
     * This ensures keys are not readable in the database but can be decrypted by the app
     * when the user is logged in.
     */
    encryptKeys: (keys: Partial<Record<ApiKeyProvider, string>>, userId: string): string => {
        const json = JSON.stringify(keys);
        const secret = `${userId}-${SALT}`;
        return CryptoJS.AES.encrypt(json, secret).toString();
    },

    /**
     * Decrypts API keys using the user's ID.
     */
    decryptKeys: (encryptedData: string, userId: string): Partial<Record<ApiKeyProvider, string>> => {
        try {
            const secret = `${userId}-${SALT}`;
            const bytes = CryptoJS.AES.decrypt(encryptedData, secret);
            const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
            if (!decryptedData) return {};
            return JSON.parse(decryptedData);
        } catch (error) {
            console.error('Failed to decrypt API keys:', error);
            return {};
        }
    },

    /**
     * Saves user preferences, including encrypted API keys, to Firestore.
     */
    saveUserPreferences: async (userId: string, keys: Partial<Record<ApiKeyProvider, string>>) => {
        try {
            const encryptedKeys = UserService.encryptKeys(keys, userId);
            const userRef = doc(db, 'users', userId);

            await setDoc(userRef, {
                encryptedApiKeys: encryptedKeys,
                updatedAt: Date.now(),
                onboardingCompleted: true
            }, { merge: true });
        } catch (error) {
            console.error('Error saving user preferences:', error);
            throw error;
        }
    },

    /**
     * Retrieves user preferences from Firestore and decrypts keys.
     */
    getUserPreferences: async (userId: string): Promise<Partial<Record<ApiKeyProvider, string>> | null> => {
        try {
            const userRef = doc(db, 'users', userId);
            const docSnap = await getDoc(userRef);

            if (docSnap.exists()) {
                const data = docSnap.data() as UserPreferences;
                if (data.encryptedApiKeys) {
                    return UserService.decryptKeys(data.encryptedApiKeys, userId);
                }
            }
            return null;
        } catch (error) {
            console.error('Error getting user preferences:', error);
            return null;
        }
    },

    /**
     * Check if user has completed onboarding
     */
    hasCompletedOnboarding: async (userId: string): Promise<boolean> => {
        try {
            const userRef = doc(db, 'users', userId);
            const docSnap = await getDoc(userRef);
            return docSnap.exists() && !!docSnap.data()?.onboardingCompleted;
        } catch {
            return false;
        }
    },

    /**
     * Sync user profile to Firestore on login
     */
    syncUserProfile: async (user: any) => {
        try {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, {
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                lastLoginAt: Date.now(),
                updatedAt: Date.now()
            }, { merge: true });
        } catch (error) {
            console.error('Error syncing user profile:', error);
        }
    },

    /**
     * Track LLM usage
     */
    trackUsage: async (userId: string, usage: {
        provider: string;
        model: string;
        tokens: number;
        cost?: number;
        projectId?: string;
    }) => {
        try {
            const usageRef = collection(db, 'users', userId, 'usage');
            await setDoc(doc(usageRef), {
                ...usage,
                timestamp: Date.now()
            });
        } catch (error) {
            console.error('Error tracking usage:', error);
        }
    }
};
